import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, AlertCircle, CheckCircle2, ArrowRight, Key } from 'lucide-react';
import Header from '../components/Header';
import { API } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [resetPath, setResetPath] = useState(null);
  const { t } = useLang();

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      const url = res.data?.reset_url;
      if (url) {
        try {
          const u = new URL(url, window.location.origin);
          setResetPath(`${u.pathname}${u.search}`);
        } catch {
          setResetPath(url);
        }
      }
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || t('somethingWentWrong'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0e10] text-white">
      <Header />
      <main className="px-4 py-10">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8 fade-in-up">
            <h1 className="heading-font text-4xl font-black tracking-wide">
              {t('forgotTitle')}
            </h1>
            <p className="mt-2 text-white/70 text-sm">
              {t('forgotSub')}
            </p>
          </div>

          {sent ? (
            <div className="fade-in-up space-y-4" data-testid="forgot-sent">
              <div className="p-5 bg-[#1a1b1e] border border-[#2a2b2f] rounded-md">
                <div className="flex items-center gap-2 text-[#ff4e15] font-semibold">
                  <CheckCircle2 size={18} /> {t('readyToReset')}
                </div>
                <p className="mt-2 text-sm text-white/70">
                  {t('resetPrepared1')} <span className="text-white">{email}</span> {t('resetPrepared2')}
                </p>
              </div>

              {resetPath ? (
                <Link
                  to={resetPath}
                  className="w-full py-4 rounded-sm font-bold uppercase tracking-wider text-white bg-[#ff4e15] hover:bg-[#ff6033] transition-colors flex items-center justify-center gap-2 pulse-orange"
                  data-testid="reset-my-password-btn"
                >
                  <Key size={16} /> {t('resetMyPassword')} <ArrowRight size={14} />
                </Link>
              ) : (
                <div className="p-4 bg-[#141518] border border-[#2a2b2f] rounded-md text-sm text-white/70">
                  {t('googleNoPassword')}
                </div>
              )}

              <p className="text-center text-[10px] text-white/40">
                {t('resetLinkExpiry')}
              </p>

              <Link
                to="/login"
                className="block text-center text-sm text-white/60 hover:text-[#ff4e15] transition-colors"
              >
                {t('backToSignIn')}
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('yourEmailPlaceholder')}
                  required
                  className="w-full pl-10 pr-4 py-3 bg-[#1a1b1e] border border-[#2a2b2f] focus:border-[#ff4e15] outline-none rounded-sm text-white transition-colors"
                  data-testid="forgot-email-input"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 text-sm text-[#ff4e15] bg-[#1f1210] border border-[#ff4e15]/40 px-3 py-2 rounded-sm">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                className="w-full py-3.5 rounded-sm font-bold uppercase tracking-wider text-white bg-[#ff4e15] hover:bg-[#ff6033] disabled:opacity-60 transition-colors"
                data-testid="forgot-submit-btn"
              >
                {busy ? t('pleaseWait') : t('continue')}
              </button>

              <p className="text-center text-sm text-white/60">
                {t('rememberedIt')}{' '}
                <Link to="/login" className="text-[#ff4e15] hover:underline">{t('signInBtn')}</Link>
              </p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default ForgotPassword;
